import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import DahliasShell from "../components/dahlias/DahliasShell";
import api from "../store/api";
import { useCart } from "../store/CartContext";
import { Button } from "../components/ui/button";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/products/${slug}`).then((r) => {
      if (!alive) return;
      setP(r.data);
      const firstWithStock = (r.data.variants || []).find((v) => v.stock > 0);
      setVariantId(firstWithStock ? firstWithStock.id : (r.data.variants?.[0]?.id ?? null));
    }).catch(() => {
      if (alive) setP(false);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return <DahliasShell><div className="max-w-5xl mx-auto px-5 py-16 text-center">Loading…</div></DahliasShell>;
  }
  if (!p) {
    return (
      <DahliasShell>
        <div className="max-w-5xl mx-auto px-5 py-16 text-center">
          <h1 className="font-display text-3xl text-[var(--dahlias-forest)]">Not found.</h1>
          <Link to="/shop" className="mt-6 inline-block text-[var(--dahlias-clay)]">← Back to shop</Link>
        </div>
      </DahliasShell>
    );
  }

  const variant = (p.variants || []).find((v) => v.id === variantId);
  const unitPrice = p.base_price + (variant?.price_delta || 0);
  const stock = variant?.stock ?? 0;
  const canAdd = !p.variants?.length || stock > 0;

  const addToCart = () => {
    addItem({
      product_id: p.id,
      variant_id: variant?.id || null,
      product_slug: p.slug,
      name: p.name,
      variant_label: variant?.label || null,
      image_url: p.image_url,
      unit_price: Number(unitPrice.toFixed(2)),
      qty,
    });
    toast.success("Added to cart");
  };

  const buyNow = () => {
    addToCart();
    navigate("/checkout");
  };

  return (
    <DahliasShell>
      <section data-testid="product-detail" className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <Link to="/shop" data-testid="product-back" className="inline-flex items-center gap-1 text-sm text-[var(--dahlias-ink)]/70 hover:text-[var(--dahlias-clay)] transition">
          <ChevronLeft className="w-4 h-4" /> Back to shop
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-6">
            <div
              className="aspect-[4/5] bg-cover bg-center bg-[var(--dahlias-cream)] border border-[var(--dahlias-ink)]/10"
              style={{ backgroundImage: `url(${p.image_url || "/photos/dahlia-field.jpg"})` }}
              data-testid="product-image"
            />
          </div>

          <div className="lg:col-span-6">
            <span className="font-display text-[var(--dahlias-clay)] tracking-[0.3em] text-xs uppercase">{p.category}</span>
            <h1 data-testid="product-name" className="font-display text-4xl md:text-5xl lg:text-6xl text-[var(--dahlias-forest)] mt-3 leading-[1.05]">
              {p.name}
            </h1>
            <p className="mt-4 text-lg text-[var(--dahlias-ink)]/80">{p.blurb}</p>
            {p.description && (
              <p className="mt-4 text-base text-[var(--dahlias-ink)]/75 leading-relaxed">{p.description}</p>
            )}

            <div className="mt-6 flex items-baseline gap-3">
              <span data-testid="product-price" className="font-display text-3xl text-[var(--dahlias-clay)]">NZ${unitPrice.toFixed(2)}</span>
              {p.variants?.length ? (
                <span className="text-sm text-[var(--dahlias-ink)]/60">{stock > 0 ? `${stock} in stock` : "Sold out"}</span>
              ) : null}
            </div>

            {p.variants?.length ? (
              <div className="mt-6">
                <div className="font-display tracking-[0.3em] text-xs uppercase text-[var(--dahlias-clay)] mb-2">Choose option</div>
                <div className="flex flex-wrap gap-2" data-testid="product-variants">
                  {p.variants.map((v) => {
                    const out = v.stock <= 0;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => !out && setVariantId(v.id)}
                        data-testid={`product-variant-${v.id}`}
                        disabled={out}
                        className={`px-4 py-3 text-base border rounded-md text-left transition-colors ${
                          variantId === v.id
                            ? "bg-[var(--dahlias-forest)] text-[var(--dahlias-cream)] border-[var(--dahlias-forest)]"
                            : "bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)] border-[var(--dahlias-ink)]/15 hover:border-[var(--dahlias-clay)]"
                        } ${out ? "opacity-40 line-through cursor-not-allowed" : ""}`}
                      >
                        <div className="leading-tight">{v.label}</div>
                        <div className="text-xs opacity-70 mt-0.5">
                          NZ${(p.base_price + (v.price_delta || 0)).toFixed(2)}
                          {out && ", sold out"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className="mt-8 flex items-center gap-4">
              <div className="inline-flex items-center border border-[var(--dahlias-ink)]/15 rounded-full overflow-hidden">
                <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 hover:bg-[var(--dahlias-cream)]" data-testid="product-qty-minus">
                  <Minus className="w-4 h-4" />
                </button>
                <span data-testid="product-qty" className="px-4 font-medium">{qty}</span>
                <button type="button" onClick={() => setQty((q) => Math.min(stock || 99, q + 1))} className="px-3 py-3 hover:bg-[var(--dahlias-cream)]" data-testid="product-qty-plus">
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <Button
                disabled={!canAdd}
                onClick={addToCart}
                data-testid="product-add-to-cart"
                className="h-14 px-8 text-base bg-[var(--dahlias-forest)] hover:bg-[var(--dahlias-clay)] text-white rounded-full font-medium tracking-wide shadow-md"
              >
                <ShoppingBag className="w-4 h-4 mr-2" /> Add to cart
              </Button>
              <Button
                disabled={!canAdd}
                onClick={buyNow}
                data-testid="product-buy-now"
                variant="outline"
                className="h-14 px-8 text-base bg-[var(--dahlias-orange)] hover:bg-[var(--dahlias-clay)] text-white border-0 rounded-full font-medium tracking-wide"
              >
                Buy now
              </Button>
            </div>

            <p className="mt-6 text-sm text-[var(--dahlias-ink)]/60">
              Free NZ shipping over $200 · Tracked NZ Post
            </p>
          </div>
        </div>
      </section>
    </DahliasShell>
  );
}
